import React, { PropTypes, PureComponent } from 'react';
import Promise from 'bluebird';
import EmptyState from '../../common/components/EmptyState';
import _ from 'underscore';
import { Button, LoadingWrapper } from 'react-fast-ui';
import WizardNav from '../WizardNav';
import { NOTIFICATION_CONTEXT_TYPES } from '../../NotificationSystemProvider';
import { StandingInfo } from '../TourneyDataTree';
import cx from 'classnames';
import { BracketGenerator } from 'react-tournament-bracket';

class BracketPreview extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired
  };

  static propTypes = {
    bracketId: PropTypes.string.isRequired,
    homeOnTop: PropTypes.bool.isRequired
  };

  static defaultProps = {};

  state = {
    promise: null,
    error: null,
    games: null
  };

  componentDidMount() {
    this.getPreview(this.props.bracketId);
  }

  componentWillReceiveProps({ bracketId }) {
    if (bracketId !== this.props.bracketId) {
      this.getPreview(bracketId);
    }
  }

  getPreview = bracketId => {
    const { promise } = this.state;
    const { dao } = this.context;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      promise: dao.seedBrackets({ bracketIds: [ bracketId ], preview: true })
        .then(
          games => this.setState({ games })
        )
        .catch(error => this.setState({ error }))
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  render() {
    const { games, error, promise } = this.state;
    const { homeOnTop } = this.props;

    if (games === null) {
      if (!!promise) {
        return EmptyState.LOADING;
      } else if (error) {
        return <EmptyState iconProps={{ name: 'exclamation-triangle' }}>{error.message}</EmptyState>;
      } else {
        return <EmptyState>Failed to load preview</EmptyState>;
      }
    }

    return (
      <BracketGenerator homeOnTop={homeOnTop} games={games}/>
    );
  }
}

const Standings = ({ teams, poolIdRanks, onCalculateStandings, onSwapTeams }) => {
  const highlightIndex = _.chain(poolIdRanks)
    .groupBy(({ poolId }) => poolId)
    .mapObject((pairs, poolId) => _.pluck(pairs, 'rank'))
    .value();

  return (
    <div className="display-flex flex-wrap-wrap align-items-flex-start">
      {
        _.chain(teams)
          .sortBy(({ pool: { name } }) => name)
          .groupBy(({ pool: { id } }) => id)
          .map((poolTeams, poolId) => {
            const pool = poolTeams[ 0 ].pool;

            const allStandings = _.all(poolTeams, ({ standing }) => !!standing);

            return (
              <div key={poolId} className="flex-grow-1 box-shadow-sm mg-sm pd-sm flex-basis-25">
                <div>
                  {pool.name}
                  <Button icon="sort-numeric-asc" onClick={() => onCalculateStandings([ pool ])} borderless={true}/>
                </div>
                <hr className="mg-bottom-sm mg-top-none"/>
                <div>
                  {
                    _.chain(poolTeams)
                      .sortBy(({ name, standing }) => {
                        return standing ? standing.rank : name;
                      })
                      .map((team, ix, list) => {
                        const { id, standing, name } = team;

                        const last = ix === poolTeams.length - 1;
                        const first = ix === 0;
                        const next = last ? null : list[ ix + 1 ];
                        const prev = first ? null : list[ ix - 1 ];
                        const highlighted = standing ? _.contains(highlightIndex[ poolId ], standing.rank) : false;

                        return (
                          <div
                            key={id}
                            className={
                              cx('display-flex align-items-center pd-xs', { 'bg-color-blue-gray': highlighted })
                            }>
                            <div className="mg-right-sm">
                              {standing ? <StandingInfo standing={standing}/> : null}
                            </div>
                            <div className="flex-grow-1">{name}</div>
                            {
                              allStandings ? (
                                  <div>
                                    <Button
                                      icon="chevron-up" borderless={true} size="sm" disabled={first}
                                      onClick={() => onSwapTeams(prev, team)}/>
                                    <Button
                                      icon="chevron-down" borderless={true} size="sm" disabled={last}
                                      onClick={() => onSwapTeams(team, next)}/>
                                  </div>
                                ) : null
                            }
                          </div>
                        );
                      }).value()
                  }
                </div>
              </div>
            );
          })
          .value()
      }
    </div>
  );
};

export default class SeedBracketWizard extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    bracketId: PropTypes.string.isRequired,
    homeOnTop: PropTypes.bool.isRequired
  };

  static defaultProps = {};

  state = {
    promise: null,
    data: null,
    activeOption: 0
  };

  componentDidMount() {
    this.loadData(this.props.bracketId);
  }

  componentWillReceiveProps({ bracketId }) {
    if (bracketId !== this.props.bracketId) {
      this.loadData(bracketId);
    }
  }

  loadData = bracketId => {
    const { dao, onError } = this.context;
    const { promise } = this.state;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      data: null,
      promise: Promise.all([
        // get all the games in the game group
        dao.games.getAll({ gameGroupId: bracketId })
      ])
        .then(
          ([ games ]) => {
            // get all the pools we need to get teams for
            const poolIdRanks = _.chain(games)
            // get the sides out of the games
              .pluck('sides')
              .map(_.values)
              .flatten(true)
              // get the seeds out of the sides
              .pluck('seed')
              .compact()
              // get the pool seeds only
              .filter(seed => !!seed.sourcePool)

              // extract the pool ID and rank into objects
              .map(({ sourcePool, rank }) => ({ poolId: sourcePool.id, rank }))

              // get the unique poolId, rank pairs
              .uniq(({ poolId, rank }) => `${poolId}-${rank}`)

              .value();

            return poolIdRanks;
          }
        )
        .then(
          poolIdRanks => {
            const { tourneyTeams } = dao;
            return Promise.all([
              // get all the teams in the pools
              poolIdRanks.length === 0 ? [] : tourneyTeams.getAll({ poolId: _.uniq(_.pluck(poolIdRanks, 'poolId')) }),
              // pass along poolIdRanks
              poolIdRanks
            ]);
          }
        )
        .then(
          ([ teams, poolIdRanks ]) => {
            const poolIdsNoStandings = _.chain(teams)
            // group by pool ID
              .groupBy(({ pool: { id } }) => id)
              // remove any keys for which pools have standings
              .omit((teams, poolId) => _.any(teams, ({ standing }) => !standing))
              .keys()
              .value();

            return Promise.all([
              dao.calculatePoolStandings({ poolIds: poolIdsNoStandings, preview: false }),
              teams,
              poolIdRanks
            ]);
          }
        )
        .then(
          ([ teamsUpdatedStandings, teams, poolIdRanks ]) => {
            const standingsById = _.indexBy(teamsUpdatedStandings, 'id');

            this.setState({
              data: {
                teams: _.map(teams, team => standingsById[ team.id ] || team),
                poolIdRanks
              }
            });
          }
        )
        .catch(onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  updateTeams = teams => {
    const { data } = this.state;
    const byId = _.indexBy(teams, 'id');

    this.setState({ data: { ...data, teams: _.map(data.teams, team => byId[ team.id ] || team) } });
  };

  calculateAllStandings = () => {
    const { promise, data: { teams } } = this.state;
    const { dao, onError } = this.context;

    if (!!promise) {
      return;
    }

    this.setState({
      promise: dao.calculatePoolStandings({ poolIds: _.chain(teams).map(({ pool: { id } }) => id).uniq().value() })
        .then(
          this.updateTeams
        )
        .catch(onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  calculateStandings = pools => {
    const { promise } = this.state;
    const { dao, onError } = this.context;

    if (!!promise) {
      return;
    }

    this.setState({
      promise: dao.calculatePoolStandings({ poolIds: _.pluck(pools, 'id'), preview: false })
        .then(this.updateTeams)
        .catch(onError)
        .finally(() => this.setState({ promise: null }))
    });
  };

  handleSwapTeams = (teamOne, teamTwo) => {
    const { dao } = this.context;
    const { promise } = this.state;

    if (!!promise) {
      return;
    }

    this.setState({
      promise: Promise.all([
        dao.tourneyTeams.save({ ...teamOne, standing: teamTwo.standing }),
        dao.tourneyTeams.save({ ...teamTwo, standing: teamOne.standing })
      ])
        .then(
          this.updateTeams
        )
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  handleChangeOption = activeOption => this.setState({ activeOption });

  render() {
    const { data, promise, activeOption } = this.state;
    const { bracketId, homeOnTop } = this.props;

    if (data === null) {
      if (promise === null) {
        return <EmptyState>Failed to load data</EmptyState>;
      } else {
        return EmptyState.LOADING;
      }
    }

    const { teams, poolIdRanks } = data;

    return (
      <LoadingWrapper loading={!!promise}>
        <WizardNav
          options={[ 'Standings', 'Preview' ]} activeOption={activeOption}
          onChangeActiveOption={this.handleChangeOption}/>
        <hr />
        {
          activeOption === 0 && (
              teams.length > 0 ? (
                  <div>
                    <div className="text-center mg-bottom-sm">
                      <Button text="Calculate Standings" onClick={this.calculateAllStandings} icon="sort-numeric-asc"/>
                    </div>
                    <Standings
                      teams={teams} onCalculateStandings={this.calculateStandings}
                      poolIdRanks={poolIdRanks}
                      onSwapTeams={this.handleSwapTeams}/>
                  </div>
                ) : <EmptyState>No pools are relevant to this bracket</EmptyState>
          )
        }

        {
          activeOption === 1 ? (
              <BracketPreview homeOnTop={homeOnTop} bracketId={bracketId}/>
            ) : null
        }
      </LoadingWrapper>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/components/wizards/SeedBracketWizard.jsx